import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { PageHeader } from "../components/common/ui";
import { cn } from "../lib/utils";
import { Sparkles, Send, ChevronRight } from "lucide-react";

const SUGGESTIONS = [
  "Why did Whitefield SLA drop last week?",
  "Which hub is underperforming and why?",
  "Why is factory capacity reaching 94%?",
  "Which customers are at risk of churning?",
  "How much time did AI save this month?",
  "Which AI agent delivers the highest ROI?",
  "What are the biggest business risks this week?",
];

export default function AskYourBusiness() {
  const [params] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const started = useRef(false);

  const send = async (question) => {
    if (!question.trim() || loading) return;
    setMessages((m) => [...m, { role: "user", text: question }]);
    setQ("");
    setLoading(true);
    try {
      const { data } = await api.post("/ask", { question });
      setMessages((m) => [...m, { role: "ai", data }]);
    } catch {
      setMessages((m) => [...m, { role: "ai", data: { answer: "Sorry, I couldn't process that right now.", confidence: 0 } }]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initial = params.get("q");
    if (initial && !started.current) { started.current = true; send(initial); }
    // eslint-disable-next-line
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  return (
    <div data-testid="ask-page">
      <PageHeader title="Ask Your Business" subtitle="Your AI co-pilot combines business, operations, customer, factory, AI and knowledge data." />
      {messages.length === 0 && (
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-3"><Sparkles className="w-4 h-4 text-secondary" /> Try asking</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => send(s)} className="text-left text-sm border border-slate-200 rounded-lg px-4 py-3 hover:border-primary/40 hover:bg-accent/50 flex items-center justify-between group" data-testid="ask-suggestion">
                {s} <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-primary" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-3xl space-y-4 mt-4">
        {messages.map((m, i) => m.role === "user" ? (
          <div key={i} className="flex justify-end"><div className="bg-primary text-white text-sm rounded-lg rounded-br-sm px-4 py-2.5 max-w-[85%]">{m.text}</div></div>
        ) : (
          <AnswerCard key={i} data={m.data} />
        ))}
        {loading && <div className="text-sm text-slate-400 animate-pulse flex items-center gap-2"><Sparkles className="w-4 h-4 text-secondary" /> Analysing your business…</div>}
        <div ref={endRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); send(q); }} className="max-w-3xl sticky bottom-4 mt-6">
        <div className="relative">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask anything about your business…"
            className="w-full h-12 pl-4 pr-14 rounded-xl border border-slate-200 shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 bg-white" data-testid="ask-input" />
          <button disabled={loading} className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 grid place-items-center bg-primary text-white rounded-lg disabled:opacity-50" data-testid="ask-send">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ label, children }) {
  if (!children || (Array.isArray(children) && !children.length)) return null;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-400 mb-1">{label}</div>
      <div className="text-sm text-slate-700">{children}</div>
    </div>
  );
}

function AnswerCard({ data }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg border-l-2 border-l-secondary overflow-hidden" data-testid="ask-answer">
      <div className="p-4 bg-secondary/5">
        <div className="flex items-center gap-2 text-xs font-semibold text-primary mb-1.5"><Sparkles className="w-4 h-4" /> Answer</div>
        <p className="text-sm text-slate-800 leading-relaxed">{data.answer}</p>
      </div>
      <div className="p-4 space-y-3">
        <Section label="Evidence">
          <ul className="list-disc list-inside space-y-0.5">{(data.evidence || []).map((e, i) => <li key={i}>{e}</li>)}</ul>
        </Section>
        <Section label="Possible Cause">{data.cause}</Section>
        <Section label="Impact">{data.impact}</Section>
        <Section label="Recommendation"><span className="font-medium text-slate-800">{data.recommendation}</span></Section>
        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <div className="text-xs text-slate-400">Sources: {(data.sources || []).join(", ") || "Business Analytics Layer"}</div>
          <span className="text-xs font-semibold text-emerald-600">Confidence {data.confidence}%</span>
        </div>
      </div>
    </div>
  );
}
